<script setup lang="ts">
  import { computed, getCurrentInstance, onBeforeUnmount } from 'vue';
  import BranchesService from '@/api/services/branches.service';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { useReservationGuide } from '@/composition/reservations/reservation-guide-context';
  import { useAuthStore } from '@/composition/use-auth';
  import { vueData } from '@/VueData';
  import {
    effectiveOnlineMaxParty,
    maxBookableOnlineParty,
  } from '@/domain/reservations/reservation-capacity';
  import {
    isPlausibleEmail,
    isPlausiblePhone,
    normalizePhoneInput,
  } from '@/domain/reservations/reservation-guide';
  import { PermissionType } from '@/enums/PermissionType';
  import type { IBranch, IBranchContactDto } from '@/interfaces/IBranch';
  import type { IReservationSettingsConfiguration } from '@/interfaces/IReservationSettings';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  /** Step 4 · Avisos: the staff email and the branch's phone numbers (D9). */
  const context = useReservationGuide();
  const auth = useAuthStore();
  const restaurant = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  let alive = true;
  onBeforeUnmount(() => {
    alive = false;
  });

  const configuration = computed(() => context.configuration.value);
  const settings = computed(() => configuration.value?.settings ?? null);
  const busy = computed(() => context.busy.value);
  const canManage = computed(() =>
    auth.permissionSet.has(PermissionType.ReservationsManage)
  );
  const canEditContact = computed(() =>
    auth.permissionSet.has(PermissionType.BranchSettings)
  );
  const draft = computed(() => context.alertsDraft.value);
  const prefill = computed(() => context.alertsPrefill.value);
  /** The email was suggested from the sign-in, not stored yet nor typed over. */
  const emailSuggested = computed(
    () =>
      settings.value?.staffEmail === null &&
      prefill.value.email !== '' &&
      draft.value.email === prefill.value.email
  );
  const emailValid = computed(() => {
    const value = draft.value.email.trim();
    return value === '' || isPlausibleEmail(value);
  });
  const telValid = computed(
    () =>
      !canEditContact.value ||
      draft.value.tel.trim() === '' ||
      isPlausiblePhone(draft.value.tel)
  );
  const whatsappValid = computed(
    () =>
      !canEditContact.value ||
      draft.value.whatsapp.trim() === '' ||
      isPlausiblePhone(draft.value.whatsapp)
  );
  const ready = computed(
    () => emailValid.value && telValid.value && whatsappValid.value
  );
  const emailError = computed(() =>
    emailValid.value ? undefined : tr('reservations.guide.alerts.emailInvalid')
  );
  const telError = computed(() =>
    telValid.value ? undefined : tr('reservations.guide.alerts.numberInvalid')
  );
  const whatsappError = computed(() =>
    whatsappValid.value
      ? undefined
      : tr('reservations.guide.alerts.numberInvalid')
  );
  const onlineMax = computed(() => {
    const cfg = configuration.value;
    return cfg
      ? effectiveOnlineMaxParty(
          cfg.settings.minPartyOnline,
          cfg.settings.maxPartyOnline,
          maxBookableOnlineParty(
            context.tablesDraft.tables.value,
            cfg.tableGroups
          )
        )
      : 0;
  });
  const whatsappHint = computed(() =>
    tr(
      canEditContact.value
        ? 'reservations.guide.alerts.whatsappHint'
        : 'reservations.guide.alerts.contactLocked',
      { max: onlineMax.value }
    )
  );
  const phoneHint = computed(() =>
    canEditContact.value
      ? undefined
      : tr('reservations.guide.alerts.contactLocked')
  );
  const guestsText = computed(() => {
    const value = settings.value;
    if (!value || !value.remindersEnabled)
      return tr('reservations.guide.alerts.guests');
    return tr(
      value.whatsappAvailable && value.whatsappEnabled
        ? 'reservations.guide.alerts.guestsReminderWhatsapp'
        : 'reservations.guide.alerts.guestsReminder'
    );
  });

  function current(configurationAtStart: IReservationSettingsConfiguration) {
    const scope = context.scope();
    return (
      alive &&
      !!scope &&
      scope.restaurantId === configurationAtStart.settings.restaurantId &&
      scope.branchId === configurationAtStart.settings.branchId
    );
  }
  function setField(field: 'email' | 'tel' | 'whatsapp', value: string) {
    context.alertsDraft.value = {
      ...context.alertsDraft.value,
      [field]: value,
    };
  }
  /** The reminder settings stay in the General card: leave the guide there. */
  function openGeneral() {
    void context.exit({ tab: 'settings', section: 'general' });
  }
  /** Every copy of the branch (legacy lists and the store) shows the new numbers. */
  function applyBranchContact(updated: IBranch) {
    const patchBranch = (branch: IBranch): IBranch =>
      branch.id === updated.id
        ? { ...branch, tel: updated.tel, whatsapp: updated.whatsapp }
        : branch;
    if (vueData.branches)
      vueData.setBranches(
        vueData.branches.map(patchBranch),
        (vueData.allBranches ?? vueData.branches).map(patchBranch)
      );
    const selected = restaurant.selectedBranch;
    if (selected && selected.id === updated.id)
      vueData.setCurrentBranch(patchBranch(selected));
  }
  /** Writes only what changed: the email as a settings patch, the numbers as a contact patch. */
  async function continueStep() {
    const cfg = configuration.value;
    if (!cfg || !ready.value || busy.value) return;
    const values = { ...draft.value };
    // The numbers belong to the branch the step was filled for, whatever is selected later.
    const branchId = cfg.settings.branchId;
    const result = await context.run(async () => {
      const email = values.email.trim().toLowerCase();
      const stored = (cfg.settings.staffEmail ?? '').trim().toLowerCase();
      if (email !== stored) {
        if (!(await context.savePatch({ staffEmail: email || null })))
          return false;
        // Stored even if the contact write below fails: the guard must not call it unsaved.
        context.commitAlerts({ email: values.email });
      }
      if (canEditContact.value) {
        const patch: IBranchContactDto = {};
        if (values.tel.trim() !== prefill.value.tel.trim())
          patch.tel = normalizePhoneInput(values.tel) || null;
        if (values.whatsapp.trim() !== prefill.value.whatsapp.trim())
          patch.whatsapp = normalizePhoneInput(values.whatsapp) || null;
        if (patch.tel !== undefined || patch.whatsapp !== undefined) {
          if (!current(cfg)) return false;
          const updated = await BranchesService.updateContact(branchId, patch);
          // The copies are keyed by id: safe to patch whatever branch is on screen now.
          applyBranchContact(updated);
          if (!current(cfg)) return false;
        }
      }
      context.commitAlerts();
      return true;
    }, tr('reservations.settings.saveError'));
    if (result && current(cfg)) context.next();
  }
  /** Enter inside a field continues, never while a field is invalid. */
  function submit() {
    if (ready.value) void continueStep();
  }

  defineExpose({ ready, continue: continueStep });
</script>

<template>
  <div class="reservations-guide-step-alerts">
    <h2 class="step-title">{{ $t('reservations.guide.alerts.title') }}</h2>
    <p class="step-help">
      {{
        $t(
          canManage
            ? 'reservations.guide.alerts.help'
            : 'reservations.guide.alerts.helpNoManage'
        )
      }}
    </p>
    <div class="alert-fields">
      <PosUiField
        :label="$t('reservations.guide.alerts.email')"
        input-id="reservations-guide-email"
        :error="emailError"
        :hint="
          emailSuggested ? $t('reservations.guide.alerts.emailHint') : undefined
        "
      >
        <PosUiInput
          id="reservations-guide-email"
          :value="draft.email"
          type="email"
          inputmode="email"
          autocomplete="email"
          :maxlength="255"
          :disabled="busy"
          @input="setField('email', $event)"
          @enter="submit"
        />
      </PosUiField>
      <PosUiField
        :label="$t('reservations.guide.alerts.phone')"
        input-id="reservations-guide-tel"
        :error="telError"
        :hint="phoneHint"
      >
        <PosUiInput
          id="reservations-guide-tel"
          :value="draft.tel"
          type="tel"
          inputmode="tel"
          autocomplete="tel"
          :maxlength="64"
          :readonly="!canEditContact"
          :disabled="busy"
          @input="setField('tel', $event)"
          @enter="submit"
        />
      </PosUiField>
      <PosUiField
        :label="$t('reservations.guide.alerts.whatsapp')"
        input-id="reservations-guide-whatsapp"
        :error="whatsappError"
        :hint="whatsappHint"
      >
        <PosUiInput
          id="reservations-guide-whatsapp"
          :value="draft.whatsapp"
          type="tel"
          inputmode="tel"
          autocomplete="tel"
          :maxlength="64"
          :readonly="!canEditContact"
          :disabled="busy"
          @input="setField('whatsapp', $event)"
          @enter="submit"
        />
      </PosUiField>
    </div>
    <div class="guests-row">
      <PosUiIcon name="bell-outline" :size="22" />
      <p class="guests-text">{{ guestsText }}</p>
      <PosUiButton
        color="neutral"
        variant="ghost"
        :disabled="busy"
        @click="openGeneral"
        >{{ $t('reservations.guide.change') }}</PosUiButton
      >
    </div>
  </div>
</template>

<style scoped lang="scss">
  .reservations-guide-step-alerts {
    @apply tw-flex tw-flex-col tw-gap-4;
    p {
      @apply tw-m-0;
    }
    .step-title {
      @apply tw-m-0 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-neutral-900;
    }
    .step-help {
      @apply tw-text-sm tw-leading-relaxed tw-text-neutral-500;
    }
    .alert-fields {
      @apply tw-flex tw-flex-col tw-gap-4;
    }
    .guests-row {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-rounded-lg tw-bg-canvas tw-px-4 tw-py-3;
      .pos-ui-icon {
        @apply tw-shrink-0 tw-text-neutral-500;
      }
    }
    .guests-text {
      @apply tw-min-w-0 tw-flex-1 tw-text-sm tw-text-neutral-700;
    }
  }
</style>
